<template>
  <Dialog
    v-model:visible="localVisible"
    :header="title"
    modal
    :closable="false"
    maximizable
    :style="{ width: '90rem' }"
    :breakpoints="{ '1199px': '80vw', '575px': '90vw' }"
    ref="addMoreMenuOptionsDialog"
  >
    <div
      class="overflow-y-auto pb-16"
      v-if="localPageObj && localPageObj.id"
    >
      <!-- Page Content -->
      <component
        :is="'PageBuilderComponent'"
        :savedResValue="pageResponseData"
        :page="localPageObj"
        :templateData="parentTemplateData"
        :updateLocalStore="true"
        ref="pageBuilderComponent"
      />
    </div>
    <template #footer>
      <div class="flex justify-end gap-2 pt-3">
        <Button
          type="button"
          label="Cancel"
          icon="pi pi-times"
          severity="secondary"
          @click="onCancelClick"
        ></Button>
        <Button
          type="button"
          label="Save"
          icon="pi pi-check-circle"
          severity="success"
          @click="onAddNewSaveBtnClick"
        ></Button>
      </div>
    </template>
  </Dialog>
  <!-- Error Dialog Component -->
  <component
    :is="'ErrorDialog'"
    :visible="errorObj.isVisible"
    :message="errorObj.errorMsg"
    :onConfirm="onErrorDialogClose"
    @update:visible="errorObj.isVisible = $event"
  />
  <FullScreenOverlayLoader
    :isLoading="loaderObj.isLoading"
    :message="loaderObj.loaderMsg"
  />
</template>

<script>
  import { defineComponent, ref, watch, onMounted, computed } from "vue";
  import { storeToRefs } from "pinia";
  import Constants from "@/utils/constants";
  import Helper from "@/utils/helper";
  import { useFormTemplateResponseStore } from "../stores/formTemplateResponseStore";
  import FullScreenOverlayLoader from "@/components/FullScreenOverlayLoader.vue";

  export default defineComponent({
    name: "AddMoreMenuOptionsComponent",
    components: {
      FullScreenOverlayLoader
    },
    props: {
      pageObj: {
        type: Object,
        required: true
      },
      title: {
        type: String,
        default: ""
      },
      visible: {
        type: Boolean,
        required: true
      },
      onSaveClick: {
        type: Function,
        required: true
      },
      // Add a new prop to control whether to call onSaveClick on visibility
      callOnSaveOnVisible: {
        type: Boolean,
        default: true
      },
      templateData: {
        type: Object,
        required: false,
        default: null
      },
      parentTemplateData: {
        type: Object,
        required: false,
        default: null
      },
      onCancelClick: {
        type: Function,
        required: false,
        default: null
      }
    },
    setup(props, { emit }) {
      const formTemplateResponseStore = useFormTemplateResponseStore();
      const { response, templateId } = storeToRefs(formTemplateResponseStore);
      const localVisible = ref(props.visible);
      const localPageObj = ref(props.pageObj);
      const pageBuilderComponent = ref(null);
      const errorObj = ref({
        isVisible: false,
        errorMsg: ""
      });
      const loaderObj = ref({
        isLoading: false,
        loaderMsg: ""
      });
      const addMoreMenuOptionsDialog = ref(null);

      // Backup state for dialog changes
      const dialogBackupState = ref(null);
      const isDialogClosingProgrammatically = ref(false);

      // Computed property to get the response data for the current page
      const pageResponseData = computed(() => {
        if (!localPageObj.value?.id || !response.value || !templateId.value) {
          return {};
        }

        // Get the response data for this specific page
        const pageData = Helper.findAndSetKeyInObject(
          response.value,
          localPageObj.value.id
        );

        return pageData || {};
      });

      const onErrorDialogClose = () => {
        errorObj.value.isVisible = false;
        errorObj.value.errorMsg = "";
      };

      // Validation function that treats the dialog as its own page
      // This ensures that OPEN_ADD_MORE_MENU dialogs have their own independent validation context
      const validatePageBeforeAction = async () => {
        let isValid = true;
        let firstInvalidField = null;

        console.log("Validating AddMoreMenu dialog as independent page:", {
          pageId: localPageObj.value?.id,
          hasTemplateData: !!props.templateData,
          hasParentTemplateData: !!props.parentTemplateData,
          hasSettings: !!props.parentTemplateData?.settings
        });

        // Validate the page builder component (includes page validation)
        if (pageBuilderComponent.value) {
          const validation = await pageBuilderComponent.value.validatePage();
          console.log("PageBuilder validation result:", validation);
          if (!validation.isValid) {
            isValid = false;
            firstInvalidField = validation.invalidFields[0];
          }
        }

        // Additional page validation specific to the dialog
        // This ensures the dialog uses the parent page's validation rules from template settings
        if (props.parentTemplateData?.settings && localPageObj.value?.id) {
          const pageValidationResult = Helper.processPageValidation(
            props.parentTemplateData.settings,
            localPageObj.value.id,
            formTemplateResponseStore.response
          );

          console.log("Dialog page validation result:", pageValidationResult);

          if (!pageValidationResult.isValid) {
            isValid = false;
            // If we don't have a firstInvalidField from component validation, use the first one from page validation
            if (
              !firstInvalidField &&
              pageValidationResult.invalidFields.length > 0
            ) {
              firstInvalidField = pageValidationResult.invalidFields[0];
            }
          }
        }

        if (!isValid && firstInvalidField) {
          // Scroll to first invalid field and show error message
          if (pageBuilderComponent.value) {
            pageBuilderComponent.value.scrollToInvalidField(firstInvalidField);
          }
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg =
            "Please fill in all required fields correctly";
        }

        console.log("Final validation result:", { isValid, firstInvalidField });
        return isValid;
      };

      // Watch for visibility changes from parent to keep local state in sync
      watch(
        () => props.pageObj,
        async (newVal) => {
          localPageObj.value = newVal;
        }
      );

      watch(
        () => props.visible,
        async (newVal) => {
          if (newVal) {
            // Save the current response state before opening dialog
            dialogBackupState.value = Helper.deepCopy(
              formTemplateResponseStore.response
            );
            console.log("dialogBackupState", dialogBackupState.value);
            isDialogClosingProgrammatically.value = false;

            // Call onSaveClick with setup parameter if enabled
            if (props.callOnSaveOnVisible) {
              try {
                await props.onSaveClick({ setup: true });
              } catch (error) {
                console.error("Error calling onSaveClick on visible:", error);
              }
            }
          }
          localVisible.value = newVal;
        },
        {
          immediate: true
        }
      );

      onMounted(() => {
        if (addMoreMenuOptionsDialog.value.maximized) return;

        addMoreMenuOptionsDialog.value.maximize();
      });

      watch(localVisible, (newVal) => {
        // If dialog was closed externally (not through our buttons) and we have backup state
        if (
          newVal === false &&
          dialogBackupState.value &&
          !isDialogClosingProgrammatically.value
        ) {
          // Revert changes
          console.log("dialogBackupState watch", dialogBackupState.value);
          formTemplateResponseStore.setInitialResponse(dialogBackupState.value);
          dialogBackupState.value = null;
        }
        // Reset the flag after the change
        isDialogClosingProgrammatically.value = false;
        emit("update:visible", newVal); // Emit the updated value to the parent
      });

      const onCancelClick = () => {
        try {
          loaderObj.value.isLoading = true;
          loaderObj.value.loaderMsg = "Closing dialog...";
          // Revert to the backup state
          isDialogClosingProgrammatically.value = true;
          if (dialogBackupState.value) {
            formTemplateResponseStore.setInitialResponse(
              dialogBackupState.value
            );
          }
          localVisible.value = false;
          dialogBackupState.value = null;
          if (props.onCancelClick) {
            props.onCancelClick();
          }
        } catch (error) {
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
        } finally {
          loaderObj.value.isLoading = false;
          loaderObj.value.loaderMsg = "";
        }
      };

      const onAddNewSaveBtnClick = async () => {
        try {
          loaderObj.value.isLoading = true;
          loaderObj.value.loaderMsg = "Saving page...";
          console.log("loaderObj :", loaderObj.value);
          // Validate the page before allowing save
          const isValid = await validatePageBeforeAction();
          if (!isValid) {
            return; // Don't proceed with save if validation fails
          }

          // Keep the changes - no need to revert
          isDialogClosingProgrammatically.value = true;
          localVisible.value = false;
          dialogBackupState.value = null;
          await props.onSaveClick({ setup: false });
        } catch (error) {
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
        } finally {
          loaderObj.value.isLoading = false;
          loaderObj.value.loaderMsg = "";
          console.log("loaderObj :", loaderObj.value);
        }
      };

      return {
        Helper,
        Constants,
        localVisible,
        localPageObj,
        pageBuilderComponent,
        pageResponseData,
        errorObj,
        loaderObj,
        onErrorDialogClose,
        onCancelClick,
        onAddNewSaveBtnClick,
        dialogBackupState,
        isDialogClosingProgrammatically,
        addMoreMenuOptionsDialog
      };
    }
  });
</script>
